/**
 * Per-user 织灵 account connection API (OpenSpec: loom-live-runaction.md, L-0).
 * Mirrors the fetch + authHeaders pattern of integrationsApi. The token never
 * comes back to the client — status returns only a mask.
 */
import { authHeaders } from "./auth";

export interface LoomConnection {
  connected: boolean;
  loginName?: string;
  tokenMask?: string;
  connectedAt?: number;
}

export const loomApi = {
  /** Current connection status for the logged-in user. */
  status: async (): Promise<LoomConnection> => {
    const res = await fetch("/api/loom/connect", { headers: authHeaders() });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return ((await res.json()) as { connection: LoomConnection }).connection;
  },

  /** Connect via login-proxy. The password is sent once and exchanged server-side; never stored. */
  connect: async (loginName: string, password: string): Promise<LoomConnection> => {
    const res = await fetch("/api/loom/connect", {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ loginName, password }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return ((await res.json()) as { connection: LoomConnection }).connection;
  },

  /** Disconnect (removes the stored token). */
  disconnect: async (): Promise<boolean> => {
    const res = await fetch("/api/loom/connect", { method: "DELETE", headers: authHeaders() });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return ((await res.json()) as { ok?: boolean }).ok ?? false;
  },

  /**
   * Run a prompt in the user's loom as a live SSE stream. Calls `onEvent` per
   * frame (session | update | end | error). Resolves when the stream closes.
   */
  run: async (
    prompt: string,
    onEvent: (event: string, data: unknown) => void,
    opts: { loomSessionId?: string; signal?: AbortSignal } = {},
  ): Promise<void> => {
    const res = await fetch("/api/loom/run", {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ prompt, ...(opts.loomSessionId !== undefined ? { loomSessionId: opts.loomSessionId } : {}) }),
      ...(opts.signal !== undefined ? { signal: opts.signal } : {}),
    });
    if (res.status === 409) {
      onEvent("error", { message: "not_connected" });
      return;
    }
    if (!res.ok || res.body === null) {
      onEvent("error", { message: `http_${res.status}` });
      return;
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = "";
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      const frames = buf.split("\n\n");
      buf = frames.pop() ?? "";
      for (const frame of frames) {
        let ev = "message";
        let data = "";
        for (const line of frame.split("\n")) {
          if (line.startsWith("event:")) ev = line.slice(6).trim();
          else if (line.startsWith("data:")) data += line.slice(5).trim();
        }
        if (data !== "") {
          try {
            onEvent(ev, JSON.parse(data));
          } catch {
            /* ignore a partial/non-JSON frame */
          }
        }
      }
    }
  },

  /**
   * Persist a run's lifecycle on its turn (annotation kind='loom-run'), so it
   * survives reload + can be continued. Best-effort: the live render works
   * regardless; persistence is what makes a reload show the run.
   */
  record: async (payload: {
    conversationId: string;
    /** loom user_session_id — the durable, unique key (adeworker via loom-cli). */
    loomSessionId: string;
    status: "running" | "done" | "error" | "stopped";
    prompt: string;
    turnAnchor?: number;
    blocks?: unknown[];
  }): Promise<void> => {
    try {
      await fetch("/api/loom/run/record", {
        method: "POST",
        headers: authHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify(payload),
      });
    } catch {
      /* best-effort; the in-memory live render still works */
    }
  },

  /** Stop an in-flight run: cancel the agent task in the user's 织灵 (#1). */
  cancel: async (loomSessionId: string): Promise<void> => {
    try {
      await fetch("/api/loom/run/cancel", {
        method: "POST",
        headers: authHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ loomSessionId }),
      });
    } catch {
      /* best-effort */
    }
  },
};
